import React, { useRef, useState } from 'react';
import { Move } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { headshotKey, saveHeadshotFocus, useHeadshotFocus } from '../lib/headshotFocus';

const clamp = (v) => Math.max(0, Math.min(100, v));

export const Headshot = ({ name, src, alt, className = '', defaultY = 20 }) => {
  const key = headshotKey(name);
  const { focus, isStaff } = useHeadshotFocus();
  const saved = focus[key];
  const [drag, setDrag] = useState(null);
  const ref = useRef(null);
  const pos = drag ? drag.pos : { x: saved?.x ?? 50, y: saved?.y ?? defaultY };

  const onPointerDown = (e) => {
    if (!isStaff || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDrag({ startX: e.clientX, startY: e.clientY, origin: pos, pos, moved: false });
  };
  const onPointerMove = (e) => {
    if (!drag) return;
    const rect = ref.current.getBoundingClientRect();
    const pos2 = { x: clamp(drag.origin.x - ((e.clientX - drag.startX) / rect.width) * 100), y: clamp(drag.origin.y - ((e.clientY - drag.startY) / rect.height) * 100) };
    setDrag({ ...drag, pos: pos2, moved: true });
  };
  const onPointerUp = async (e) => {
    if (!drag) return;
    e.stopPropagation();
    const final = drag.pos;
    const moved = drag.moved;
    setDrag(null);
    if (!moved) return;
    try {
      await saveHeadshotFocus(key, Math.round(final.x * 10) / 10, Math.round(final.y * 10) / 10);
      toast({ title: 'Photo position saved', description: `${name} — faces now sit where you placed them.` });
    } catch {
      toast({ title: 'Could not save photo position', variant: 'destructive' });
    }
  };

  return (
    <div ref={ref} className="absolute inset-0" data-testid={`headshot-${key}`}>
      <img
        src={src}
        alt={alt || name}
        draggable={false}
        className={`w-full h-full object-cover select-none ${isStaff ? (drag ? 'cursor-grabbing' : 'cursor-grab') : ''} ${className}`}
        style={{ objectPosition: `${pos.x}% ${pos.y}%`, touchAction: isStaff ? 'none' : undefined }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => setDrag(null)}
        onClickCapture={(e) => { if (isStaff) { e.stopPropagation(); e.preventDefault(); } }}
        loading="lazy"
        decoding="async"
        data-testid={`headshot-img-${key}`}
      />
      {isStaff && (
        <div className="absolute inset-x-0 top-[40%] z-20 flex justify-center pointer-events-none"><div className="flex items-center gap-1 bg-black/65 text-white font-heading uppercase tracking-widest text-[9px] px-2 py-1" data-testid={`headshot-drag-hint-${key}`}>
          <Move className="w-3 h-3" aria-hidden="true" /> {drag ? `${Math.round(pos.x)}% · ${Math.round(pos.y)}%` : 'Drag to reposition'}
        </div></div>
      )}
    </div>
  );
};
